import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type { AxiosRequestConfig } from "axios"
import { z } from "zod"
import * as Sentry from "@sentry/react"
import client from "../helpers/client"

const baseApiResponseSchema = z.object({
  data: z.unknown(),
  message: z.string().optional(),
  success: z.boolean().default(true),
})

const paginatedResponseSchema = z.object({
  items: z.array(z.unknown()),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
})

async function apiRequest<T>(
  url: string,
  schema: z.ZodSchema<T>,
  options?: AxiosRequestConfig,
): Promise<T> {
  try {
    const response = await client.request({ url, method: "get", ...options })

    const baseResult = baseApiResponseSchema.safeParse(response.data)
    if (!baseResult.success) {
      console.error("Invalid base API structure", baseResult.error)
      throw new Error("Unexpected API response format")
    }

    const { data, message, success } = baseResult.data
    if (!success) {
      throw new Error(message || "Request failed")
    }

    const result = schema.safeParse(data)
    if (!result.success) {
      console.error("Zod validation failed", result.error)
      Sentry.captureException(result.error, { extra: { url, data } })
      return data as T
    }

    return result.data
  } catch (err: any) {
    console.error("API request failed", err)
    throw err instanceof Error ? err : new Error("Unknown API error")
  }
}

export interface PaginatedResult<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface CrudFilters {
  page?: number
  page_size?: number
  [key: string]: unknown
}

export function useCrudList<T>(
  queryKey: string,
  url: string,
  itemSchema: z.ZodSchema<T>,
  filters?: CrudFilters,
) {
  const paginatedSchema = z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    page_size: z.number(),
  })

  return useQuery<PaginatedResult<T>, Error>({
    queryKey: [queryKey, filters],
    queryFn: () =>
      apiRequest(url, paginatedSchema, {
        params: filters,
      }),
    staleTime: 1000 * 60 * 2,
  })
}

export function useCrudItem<T>(
  queryKey: string,
  url: string,
  itemSchema: z.ZodSchema<T>,
  id?: number | string | null,
) {
  return useQuery<T, Error>({
    queryKey: [queryKey, id],
    queryFn: () => apiRequest(`${url}/${id}`, itemSchema),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  })
}

export function useCrudMutations<T>(
  queryKey: string,
  url: string,
  itemSchema?: z.ZodSchema<T>,
) {
  const queryClient = useQueryClient()

  const create = useMutation<T, Error, Record<string, unknown>>({
    mutationFn: (data) =>
      apiRequest(url, itemSchema ?? z.unknown() as z.ZodSchema<T>, {
        method: "post",
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    },
  })

  const update = useMutation<T, Error, { id: number | string; data: Record<string, unknown> }>({
    mutationFn: ({ id, data }) =>
      apiRequest(`${url}/${id}`, itemSchema ?? z.unknown() as z.ZodSchema<T>, {
        method: "put",
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    },
  })

  const remove = useMutation<void, Error, number | string>({
    mutationFn: (id) =>
      apiRequest(`${url}/${id}`, z.unknown() as z.ZodSchema<void>, {
        method: "delete",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    },
  })

  return { create, update, remove }
}
